import React from 'react';
import { HeartHandshake, Target, Users, ShieldCheck, CalendarCheck, Sparkles, Handshake, Award, Compass, Stethoscope } from 'lucide-react';
import { PORTAL_URL } from '../SiteLayout';
import PageSEO from '../../components/PageSEO';

const VALUES = [
  { icon: <Award size={26} />, title: 'Excellence', text: "Nous recherchons en permanence la qualité dans les soins, les pratiques médicales, les équipements et l'organisation de notre établissement." },
  { icon: <HeartHandshake size={26} />, title: 'Humanité', text: "Nous accueillons chaque patient avec respect, écoute, empathie et bienveillance." },
  { icon: <ShieldCheck size={26} />, title: 'Intégrité', text: "Nous exerçons notre profession avec honnêteté, transparence et dans le respect des principes éthiques de la médecine." },
  { icon: <Sparkles size={26} />, title: 'Innovation', text: "Nous développons des solutions nouvelles lorsqu'elles permettent d'améliorer la qualité, l'accessibilité ou la sécurité des soins." },
  { icon: <Users size={26} />, title: "Esprit d'équipe", text: "Nous croyons que les meilleurs résultats sont obtenus grâce à la collaboration entre tous les professionnels de santé." },
  { icon: <Target size={26} />, title: 'Responsabilité', text: "Nous assumons notre rôle dans le développement sanitaire du Kongo Central et contribuons à l'amélioration durable de l'offre de soins." },
];

const ENGAGEMENTS = [
  "Accueillir chaque patient avec dignité et respect",
  "Fournir des soins conformes aux bonnes pratiques médicales",
  "Expliquer clairement les diagnostics et les traitements proposés",
  "Protéger la confidentialité des informations médicales",
  "Favoriser l'accès aux spécialistes grâce aux technologies numériques",
  "Améliorer continuellement la qualité de ses services",
];

const TIMELINE = [
  { year: '2015', label: 'Création du Centre Médical la Trinité à Matadi le 30 juin' },
  { year: '2015-2020', label: 'Développement progressif de nouvelles spécialités médicales' },
  { year: '2020', label: "Ouverture de l'extension de Boma" },
  { year: '2021', label: "Structuration entrepreneuriale — inscription au RCCM" },
  { year: '2023', label: "Construction du nouveau complexe hospitalier à Matadi" },
  { year: '2025', label: "Lancement de CMT Health Platform" },
];

export default function SiteAbout() {
  return (
    <main data-testid="site-about">
      <PageSEO
        title="À propos — Notre engagement médical à Matadi & Boma"
        description="Depuis 2015, le Centre Médical la Trinité (CMT) offre des soins spécialisés de qualité à Matadi et Boma (RDC) : gynécologie, chirurgie, pédiatrie, médecine interne, téléconsultation."
        path="/a-propos"
      />
      <div className="site-page-head">
        <div className="site-page-head-inner">
          <h1 className="site-reveal">Une médecine spécialisée fondée sur la confiance, l&apos;excellence et l&apos;innovation</h1>
          <p className="site-reveal-1">
            Depuis 2015, le Centre Médical la Trinité accompagne les populations du Kongo Central
            avec une ambition constante : offrir des soins spécialisés de qualité dans un environnement
            moderne, humain et accessible.
          </p>
        </div>
      </div>

      {/* Notre histoire */}
      <section className="site-section">
        <div className="site-split">
          <div>
            <span className="site-kicker">Notre histoire</span>
            <h2>Une conviction devenue une institution</h2>
            <p style={{ color: 'var(--site-muted)', lineHeight: 1.75, marginBottom: '1rem' }}>
              Chaque établissement de santé possède une date de création. Le Centre Médical la Trinité
              possède avant tout une histoire — née d&apos;une conviction profonde : <strong>chaque famille mérite
              d&apos;avoir accès à des soins spécialisés de qualité, près de chez elle</strong>, dans un environnement
              où la compétence médicale s&apos;allie à l&apos;écoute, au respect et à la dignité.
            </p>
            <p style={{ color: 'var(--site-muted)', lineHeight: 1.75, marginBottom: '1rem' }}>
              Cette conviction a guidé, dès l&apos;origine, le <strong>Dr Jean Robert Boffendakini</strong>, Gynécologue-Obstétricien,
              et le <strong>Dr Mamie Patricia Boffendakini</strong>, Anesthésiste-Réanimatrice, deux médecins animés par
              une même ambition : mettre leur expertise au service de la population du Kongo Central.
              Le 30 juin 2015, cette vision prend corps avec la création du Centre Médical la Trinité à Matadi.
            </p>
            <p style={{ color: 'var(--site-muted)', lineHeight: 1.75 }}>
              Cette vision s&apos;est construite grâce à l&apos;expérience acquise au sein de l&apos;Association Santé
              Maternelle et Infantile (ASMI), véritable laboratoire d&apos;engagement, de proximité avec les
              communautés et d&apos;amélioration continue des pratiques. Le Centre Médical la Trinité est
              l&apos;aboutissement naturel de cette démarche.
            </p>
          </div>
          <img src="/photos/soins_equipe.jpg" alt="Équipe soignante du Centre Médical la Trinité" loading="lazy" className="cmt-side-photo" />
        </div>
      </section>

      {/* Grandir + Innover */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <div className="site-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
            <div className="site-card">
              <div className="site-card-icon"><Handshake size={26} /></div>
              <h3>Grandir au rythme de la confiance</h3>
              <p>
                Année après année, à mesure que la confiance des patients grandissait, le Centre développait
                de nouveaux services, renforçait son équipe médicale et investissait dans des équipements
                modernes. L&apos;ouverture d&apos;une extension à Boma, puis la construction d&apos;un nouveau bâtiment
                répondant aux standards actuels de la médecine, ont marqué cette évolution.
              </p>
            </div>
            <div className="site-card">
              <div className="site-card-icon"><Sparkles size={26} /></div>
              <h3>Innover sans jamais perdre de vue l&apos;humain</h3>
              <p>
                Le projet <strong>CMT Health Platform</strong> — écosystème numérique de rendez-vous en ligne,
                dossier patient numérique, téléconsultation, paiements électroniques — poursuit un objectif
                unique : rendre les soins plus accessibles, plus fluides et plus sûrs. La téléconsultation
                permet notamment d&apos;accéder à l&apos;expertise de spécialistes exerçant dans d&apos;autres provinces
                ou à l&apos;étranger.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Frise chronologique */}
      <section className="site-section">
        <span className="site-kicker">Notre développement</span>
        <h2>Une évolution continue au service de la population</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1.25rem', marginTop: '2rem' }}>
          {TIMELINE.map((t, i) => (
            <div key={i} className="site-card" style={{ borderLeft: '4px solid var(--site-primary)', textAlign: 'left' }}>
              <strong style={{ color: 'var(--site-primary)', fontSize: '1.3rem', display: 'block', marginBottom: 6 }}>{t.year}</strong>
              <p style={{ margin: 0 }}>{t.label}</p>
            </div>
          ))}
        </div>
        <div className="cmt-home-gallery" style={{ marginTop: '2.5rem' }} data-testid="about-photos">
          <a href="/galerie" aria-label="Cour intérieure"><img src="/photos/cour_interieure_thumb.jpg" alt="Cour intérieure" loading="lazy" /></a>
          <a href="/galerie" aria-label="Salle d'attente"><img src="/photos/salle_attente_thumb.jpg" alt="Salle d'attente" loading="lazy" /></a>
          <a href="/galerie" aria-label="Réception"><img src="/photos/reception_thumb.jpg" alt="Réception" loading="lazy" /></a>
          <a href="/galerie" aria-label="Façade principale"><img src="/photos/facade_principale_thumb.jpg" alt="Façade principale" loading="lazy" /></a>
        </div>
      </section>

      {/* Mission + Vision */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <div className="site-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))' }}>
            <div className="site-card">
              <div className="site-card-icon"><Target size={26} /></div>
              <h3>Notre mission</h3>
              <p>
                Offrir à chaque patient une prise en charge médicale de qualité, fondée sur l&apos;expertise,
                l&apos;humanisme et l&apos;innovation. Nous mettons à la disposition des populations des soins
                spécialisés, des technologies adaptées et un accompagnement personnalisé tout au long
                de leur parcours de santé.
              </p>
            </div>
            <div className="site-card">
              <div className="site-card-icon"><Compass size={26} /></div>
              <h3>Notre vision</h3>
              <p>
                Devenir une référence en matière de soins spécialisés et de transformation numérique
                de la santé au Kongo Central. À travers <strong>CMT Health Platform</strong>, nous souhaitons
                faire du numérique un levier d&apos;équité en santé afin que les limites géographiques ne
                constituent plus un obstacle à l&apos;accès à une expertise médicale de haut niveau.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Valeurs */}
      <section className="site-section">
        <span className="site-kicker">Nos valeurs</span>
        <h2>Ce qui guide chacun de nos gestes</h2>
        <div className="site-grid" style={{ marginTop: '2rem' }}>
          {VALUES.map((v, i) => (
            <div className="site-card" key={i}>
              <div className="site-card-icon">{v.icon}</div>
              <h3>{v.title}</h3>
              <p>{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Engagements */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <span className="site-kicker">Nos engagements</span>
          <h2>Nos engagements envers nos patients</h2>
          <ul style={{ marginTop: '1.5rem', columnCount: 2, columnGap: '2.5rem', listStyle: 'none', padding: 0 }}>
            {ENGAGEMENTS.map((e, i) => (
              <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: '.85rem', breakInside: 'avoid' }}>
                <ShieldCheck size={20} color="var(--site-primary)" style={{ flexShrink: 0, marginTop: 3 }} />
                <span>{e}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* Mot des fondateurs */}
      <section className="site-section">
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <span className="site-kicker">Le mot des fondateurs</span>
          <h2>Notre engagement</h2>
          <blockquote style={{
            fontSize: '1.15rem', lineHeight: 1.75, fontStyle: 'italic',
            color: '#334155', margin: '2rem 0', padding: '1.5rem 2rem',
            borderLeft: '4px solid var(--site-primary)', background: '#f8fafc',
            borderRadius: 8, textAlign: 'left',
          }}>
            « Depuis la création du Centre Médical la Trinité, notre ambition est restée la même :
            offrir aux populations des soins spécialisés de qualité dans un environnement où chaque
            patient est accueilli avec compétence, respect et humanité.
            <br /><br />
            Nous croyons que la médecine évolue en permanence et que notre responsabilité est
            d&apos;évoluer avec elle, sans jamais perdre de vue l&apos;essentiel : la confiance que les
            patients nous accordent.
            <br /><br />
            Chaque projet que nous développons, chaque équipement que nous acquérons et chaque
            innovation que nous mettons en œuvre poursuivent un objectif unique : améliorer
            durablement la santé des personnes qui nous font confiance. »
          </blockquote>
          <p style={{ fontWeight: 600, color: 'var(--site-primary)' }}>Dr Jean Robert Boffendakini</p>
          <p style={{ color: 'var(--site-muted)', margin: 0 }}>Directeur Général — Centre Médical la Trinité</p>
        </div>
      </section>

      {/* Appel à l'action */}
      <section className="site-section">
        <div className="site-cta-band">
          <div>
            <h2>Découvrez un établissement engagé pour votre santé</h2>
            <p>Notre équipe est à votre disposition pour vous accompagner à chaque étape de votre parcours de soins.</p>
          </div>
          <a href={`${PORTAL_URL}?page=booking`} className="site-btn site-btn-outline" style={{ background: 'white' }} data-testid="about-cta-booking-btn">
            <CalendarCheck size={18} /> Prendre rendez-vous
          </a>
        </div>
      </section>
    </main>
  );
}
